import { Injectable, OnDestroy, inject } from "@angular/core";
import { AppSettingsService } from "@shared/app-settings-service";
import { BroadcastService } from "@shared/broadcast-service";
import { defer, filter, firstValueFrom, map, Observable, shareReplay, Subject, take, tap, timeout } from "rxjs";

export class IframeMessage {
    origin: string;
    data: any;
    constructor(origin: string, data: any) {
        this.origin = origin;
        this.data = data;
    }
}

@Injectable({
    providedIn: "root",
})
export class IframeMessagesService {
    private appSettingsService = inject(AppSettingsService);

    private allowedSenders = [];
    public initDone = false;
    public initSessionInfoOrigin: string;
    public messagesOrigin: string = null;
    public messagesOriginInitializedSubject = new Subject<any>();
    messagesOriginInitializedEvent$ = this.messagesOriginInitializedSubject.asObservable();
    public InitDoneSubject = new Subject<any>();
    InitDoneEvent$ = this.InitDoneSubject.asObservable();

    constructor() {
        window.addEventListener("message", (event) => {
            if (event.data !== undefined && event.data !== null && event.data.message !== undefined) {
                if (!this.messagesOrigin) {
                    this.messagesOrigin = event.origin;
                    this.messagesOriginInitializedSubject.next(true);
                }
                if (event.data.message === "initSessionInfo" || this.messageOriginValid(event.origin)) {
                    BroadcastService.broadcast(event.data.message, new IframeMessage(event.origin, event.data.data));
                }
            }
        });
    }

    init(): void {
        this.allowedSenders = this.appSettingsService.appSettings.AllowedSenders.split(";").filter((it) => it.trim().length > 0);
        this.InitDoneSubject.next(true);
        this.initDone = true;
    }

    public messageOriginValid(origin: string): boolean {
        return this.allowedSenders.filter((it) => this.match(it, origin)).length !== 0;
    }

    public postMessage(message: string, data: any) {
        if (this.messagesOrigin !== undefined && this.messagesOrigin !== null) {
            const msg = {
                message,
                data,
            };
            parent.postMessage(msg, this.messagesOrigin);
        } else {
            this.messagesOriginInitializedEvent$.subscribe(() => {
                const msg = {
                    message,
                    data,
                };
                parent.postMessage(msg, this.messagesOrigin);
            });
        }
    }

    public async postMessageResp(message: string, data: any, responseMessage: string, messageResponseTimeout = 600000): Promise<any> {
        const msgId = crypto.randomUUID();
        if (!data) {
            data = {};
        }
        data.msgId = msgId;

        const ack$ = BroadcastService.on<IframeMessage>(message + "Ack").pipe(
            filter((msg) => msg.data.msgId === msgId),
            take(1),
            timeout(5000),
        );

        const response$ = BroadcastService.on<IframeMessage>(responseMessage).pipe(
            tap((msg) => {
                if (responseMessage === "initSessionInfo") {
                    this.initSessionInfoOrigin = msg.origin;
                }
            }),
            filter((msg) => msg.data.msgId === msgId),
            take(1),
            map((msg) => msg.data),
            timeout(messageResponseTimeout),
        );

        // Subscribe to the response before posting/awaiting the ack so a response that
        // arrives very quickly (before or right after the ack) is not missed.
        const responsePromise = firstValueFrom(response$);
        const ackPromise = firstValueFrom(ack$);

        this.postMessage(message, data);
        // Wait for the ack first; if it never arrives, the response is not waited for.
        await ackPromise;
        return await responsePromise;
    }

    public postMessageRespObservable<TReq = void, TResp = unknown>(
        message: string,
        responseMessage: string,
        data?: TReq,
        messageResponseTimeout = 30000,
    ): Observable<TResp> {
        const msgId = crypto.randomUUID();
        const outgoing: Record<string, unknown> = { ...data, msgId };

        return defer(() => {
            this.postMessage(message, outgoing);
            return BroadcastService.on<IframeMessage>(responseMessage).pipe(
                tap((msg) => {
                    if (responseMessage === "initSessionInfo") {
                        this.initSessionInfoOrigin = msg.origin;
                    }
                }),
                filter((msg) => msg.data?.msgId === msgId),
                take(1),
                map((msg) => msg.data as TResp),
            );
        }).pipe(timeout(messageResponseTimeout));
    }

    private match(first: string, second: string): boolean {
        if (first.length === 0 && second.length === 0) {
            return true;
        }
        if (first.length > 1 && first[0] === "*" && second.length === 0) {
            return false;
        }

        if ((first.length > 1 && first[0] === "?") || (first.length !== 0 && second.length !== 0 && first[0] === second[0])) {
            return this.match(first.substring(1), second.substring(1));
        }

        if (first.length > 0 && first[0] === "*") {
            return this.match(first.substring(1), second) || this.match(first, second.substring(1));
        }
        return false;
    }
}
